import { Modal } from "semantic-ui-react";
import PropTypes from "prop-types";
import React from "react";
import { Trans } from "react-i18next";
import { Button, Tooltip } from '@weezevent/nacre';

/** View for the semantic-ui-react Modal
 * It implements a template of modal content with a trigger. It will render
 * the following props if they are present.
 * - header in a Modal.Header
 * - children in the Modal.Content
 * - a Modal.Actions component with
 *   - validateLabel in a Button if onValidate is not null
 *   - cancelLabel in a Button if onValidate is not null
 *
 * The trigger prop is an element that is always rendered, it listens to click
 * events and triggers the `onOpen` callback. It can be null, a string or any
 * React node.
 *
 * - When it is null, the trigger is not shown, the onOpen callback won't be
 * called.
 * - When it is a string, it is used as the text of a semantic-ui-react Button
 * the button is disabled if onOpen is falsy.
 * - When it is any other React element, it's rendered inside a span that
 * listen to click events.
 */
export default function SaveModal({
	header,
	trigger,
	open,
	children,
	onOpen,
	onCancel,
	onValidate,
	validateLabel,
	cancelLabel,
	disabled,
	openable,
	disabledTooltipLabel,
}) {
	if (typeof trigger === 'string') {
		trigger = <Button primary onClick={ onOpen } disabled={ !openable && !onOpen } >{ trigger }</Button>;
	} else if (trigger) {
		trigger = <span onClick={ onOpen }>{ trigger }</span>;
	}

	if(disabledTooltipLabel) {
		trigger = <Tooltip
			mini={ true }
			title={ disabledTooltipLabel }
			triggerOnHover={ true }
			visible={ disabled }
			style={ { 'float' : 'right' } }
			position="left"
		>
			{ trigger }
		</Tooltip>;
	}

	cancelLabel = cancelLabel || <Trans i18nKey="common.cancel">Cancel</Trans>;
	validateLabel = validateLabel || <Trans i18nKey="common.save">Save</Trans>;

	return <>
		{ trigger }
		<Modal closeOnDimmerClick closeOnEscape open={ open } size="small" >
			{ header ? <Modal.Header>{ header }</Modal.Header> : null }
			<Modal.Content>
				{ children }
			</Modal.Content>
			<Modal.Actions>
				{ Boolean(onCancel) && <Button inverted onClick={ onCancel } >
					{ cancelLabel }
				</Button> }
				{ Boolean(onValidate) && <Button primary disabled={ disabled } className={ disabled ? 'disabled-button' : '' } onClick={ onValidate } >
					{ validateLabel }
				</Button>
				}
			</Modal.Actions>
		</Modal>
	</>;
}

SaveModal.propTypes = {
	header: PropTypes.node,
	trigger: PropTypes.node,
	open: PropTypes.bool.isRequired,
	children: PropTypes.node.isRequired,
	onOpen: PropTypes.func,
	onCancel: PropTypes.func,
	onValidate: PropTypes.func,
	validateLabel: PropTypes.node,
	cancelLabel: PropTypes.node,
	disabled: PropTypes.bool,
	openable: PropTypes.bool,
	disabledTooltipLabel: PropTypes.string,
};

